import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, ChevronLeft, Copy } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { cn } from '@/lib/utils';
import {
  METHODOLOGY,
  type MethodologySection,
  type Metric,
  type Threshold,
} from './methodology.data';

function toneClass(tone: Threshold['tone']) {
  if (tone === 'good') return 'text-green-400';
  if (tone === 'warn') return 'text-amber-400';
  return 'text-red-400';
}

function slugHash(id: string) {
  return `#${id}`;
}

function copyToClipboard(text: string) {
  if (!text) return;
  void navigator.clipboard?.writeText(text);
}

const MetricCard: React.FC<{ metric: Metric }> = ({ metric }) => {
  return (
    <div className="rounded-lg border bg-nb-surface-2 border-nb-border-subtle p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-nb-text-primary">{metric.title}</h3>
          {metric.intent && (
            <p className="mt-1 text-sm text-nb-text-secondary">{metric.intent}</p>
          )}
        </div>

        {metric.formula && (
          <button
            type="button"
            onClick={() => copyToClipboard(metric.formula!)}
            className="shrink-0 inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-nb-border-subtle bg-nb-surface-1 text-nb-text-secondary hover:text-nb-text-primary focus:outline-none focus:ring-2 focus:ring-nb-accent focus:ring-offset-1"
            title="Copy formula"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            Copy
          </button>
        )}
      </div>

      {metric.inputs?.length ? (
        <div className="mt-3 text-xs text-nb-text-muted">
          <p className="font-semibold uppercase tracking-wide">Inputs</p>
          <ul className="mt-2 space-y-1">
            {metric.inputs.map((i) => (
              <li key={i.name} className="flex flex-wrap gap-x-2 gap-y-1">
                <span className="text-nb-text-secondary">{i.name}</span>
                {i.source && (
                  <code className="px-1 py-0.5 rounded bg-nb-surface-1 border border-nb-border-subtle">
                    {i.source}
                  </code>
                )}
                {i.note && <span>— {i.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {metric.formula && (
        <pre className="mt-3 p-3 rounded bg-nb-surface-1 border border-nb-border-subtle text-xs font-mono whitespace-pre-wrap text-nb-text-primary">
          {metric.formula}
        </pre>
      )}

      {metric.thresholds?.length ? (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-nb-text-muted">
            Thresholds
          </p>
          <ul className="mt-2 text-xs space-y-1">
            {metric.thresholds.map((t) => (
              <li key={t.label} className="text-nb-text-muted">
                <span className={cn('font-semibold', toneClass(t.tone))}>{t.label}</span>
                <span className="mx-2">·</span>
                <span>{t.range}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {metric.caveats?.length ? (
        <div className="mt-3 text-xs text-nb-text-muted">
          <p className="font-semibold uppercase tracking-wide">Notes</p>
          <ul className="mt-2 space-y-1 ml-4 list-disc">
            {metric.caveats.map((n, idx) => (
              <li key={idx}>{n}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const SectionBlock: React.FC<{ section: MethodologySection }> = ({ section }) => {
  return (
    <section
      id={section.id}
      className="scroll-mt-6 rounded-lg border border-nb-border-subtle bg-nb-surface-1 overflow-hidden border-l-4 border-l-nb-accent"
    >
      {/* Report header: clear separation from other reports */}
      <div className="border-b border-nb-border-subtle bg-nb-surface-2 px-6 py-4">
        <h2 className="text-lg font-semibold text-nb-text-primary">{section.label}</h2>
        {section.description && (
          <p className="mt-2 text-sm text-nb-text-secondary">{section.description}</p>
        )}
      </div>

      {/* Report content */}
      <div className="p-6 space-y-4">
        {section.metrics?.map((m) => (
          <MetricCard key={m.id} metric={m} />
        ))}

        {section.blocks?.map((b) => (
          <div
            key={b.title}
            className="rounded-lg border bg-nb-surface-2 border-nb-border-subtle p-4"
          >
            <h3 className="text-sm font-semibold text-nb-text-primary">{b.title}</h3>
            <ul className="mt-2 text-xs space-y-1 ml-4 list-disc text-nb-text-muted">
              {b.bullets.map((x, idx) => (
                <li key={idx}>{x}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

const CalculationMethodology: React.FC = () => {
  const location = useLocation();
  const [query, setQuery] = useState('');

  // On load / hash change, scroll to section
  useEffect(() => {
    const id = location.hash?.replace('#', '');
    if (!id) return;
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return METHODOLOGY;

    return METHODOLOGY.map((s) => {
      const metrics = s.metrics?.filter((m) => {
        const hay = [
          m.title,
          m.intent,
          m.formula,
          ...(m.inputs?.map((i) => `${i.name} ${i.source ?? ''}`) ?? []),
          ...(m.caveats ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });

      const blocks = s.blocks?.filter((b) => {
        const hay = `${b.title} ${b.bullets.join(' ')}`.toLowerCase();
        return hay.includes(q);
      });

      // Keep section if any match; show full section when no sub-filter
      if ((metrics && metrics.length) || (blocks && blocks.length)) {
        return { ...s, metrics: metrics ?? s.metrics, blocks: blocks ?? s.blocks };
      }
      return null;
    }).filter(Boolean) as MethodologySection[];
  }, [query]);

  return (
    <div className="min-h-screen p-6 bg-nb-background">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Link
            to="/app/reports"
            className="inline-flex items-center gap-2 text-sm font-medium text-nb-accent-primary hover:text-nb-accent-blue focus:outline-none focus:ring-2 focus:ring-nb-accent focus:ring-offset-2 rounded mb-4"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Back to Reports
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-8 w-8 text-nb-accent" />
            <h1 className="text-3xl font-display font-bold text-nb-text-primary">
              Report Calculation Methodology
            </h1>
          </div>

          <p className="text-sm text-nb-text-muted">
            Linkable, searchable metric definitions and data sources.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* TOC */}
          <aside className="lg:w-64 shrink-0">
            <div className="sticky top-6 rounded-lg border border-nb-border-subtle bg-nb-surface-1 p-3 space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-nb-text-muted mb-2">
                  Search
                </p>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try: float, SPI, DCMA…"
                  className="w-full px-3 py-2 rounded-md bg-nb-surface-2 border border-nb-border-subtle text-sm text-nb-text-primary placeholder:text-nb-text-muted focus:outline-none focus:ring-2 focus:ring-nb-accent"
                  aria-label="Search methodology"
                />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-nb-text-muted mb-2">
                  Contents
                </p>
                <nav aria-label="Methodology contents" className="space-y-1">
                  {METHODOLOGY.map((s) => (
                    <a
                      key={s.id}
                      href={slugHash(s.id)}
                      className={cn(
                        'block px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        'text-nb-text-secondary hover:bg-nb-surface-2 hover:text-nb-text-primary'
                      )}
                    >
                      {s.label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <DarkPanel>
              <div className="space-y-8">
                {filtered.map((s) => (
                  <SectionBlock key={s.id} section={s} />
                ))}

                {filtered.length === 0 && (
                  <div className="text-sm text-nb-text-secondary">
                    No matches for &quot;{query}&quot;.
                  </div>
                )}
              </div>
            </DarkPanel>
          </main>
        </div>
      </div>
    </div>
  );
};

export default CalculationMethodology;
